import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { API_URL } from '../utils/env';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { geminiApi } from '../services/geminiApi';
import { toast } from '../utils/toast';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, Filler } from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Add new keyframe animations at the top
const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: linear-gradient(to bottom right, #f8fafc, #f1f5f9);
  position: relative;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(45deg, 
        rgba(255, 0, 0, 0.02) 0%,
        rgba(255, 154, 0, 0.02) 10%,
        rgba(208, 222, 33, 0.02) 20%,
        rgba(79, 220, 74, 0.02) 30%,
        rgba(63, 218, 216, 0.02) 40%,
        rgba(47, 201, 226, 0.02) 50%,
        rgba(28, 127, 238, 0.02) 60%,
        rgba(95, 21, 242, 0.02) 70%,
        rgba(186, 12, 248, 0.02) 80%,
        rgba(251, 7, 217, 0.02) 90%,
        rgba(255, 0, 0, 0.02) 100%);
    background-size: 400% 400%;
    animation: rainbow 15s linear infinite;
    z-index: 0;
  }

  @keyframes rainbow {
    0% { background-position: 0% 50% }
    50% { background-position: 100% 50% }
    100% { background-position: 0% 50% }
  }
`;

const Sidebar = styled.div`
  width: ${props => props.isCollapsed ? '64px' : '240px'};
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  color: #1e293b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border-right: 1px solid rgba(148, 163, 184, 0.1);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    inset 0 0 20px rgba(255, 255, 255, 0.5);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
    animation: holographic 6s linear infinite;
    pointer-events: none;
  }

  @keyframes holographic {
    0% { transform: rotate(0deg) }
    100% { transform: rotate(360deg) }
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'space-between'};
  background: rgba(255, 255, 255, 0.8);
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #fff;
  }
`;

const NavSection = styled.div`
  padding: ${props => props.isCollapsed ? '8px 0' : '8px 12px'};
`;

const NavTitle = styled.div`
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  margin: 8px 12px;
  display: ${props => props.isCollapsed ? 'none' : 'block'};
`;

const NavItem = styled.div`
  padding: ${props => props.isCollapsed ? '12px 0' : '12px'};
  margin: 4px 8px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  color: ${props => props.active ? '#3b82f6' : '#64748b'};
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
    color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
    transition: 0.5s;
    animation: shine 3s infinite;
  }

  @keyframes shine {
    0% { transform: translateX(-100%) rotate(45deg) }
    100% { transform: translateX(100%) rotate(45deg) }
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: ${props => props.isCollapsed ? '0' : '12px'};
    transition: all 0.3s;
    filter: drop-shadow(0 0 2px rgba(59, 130, 246, 0.3));
  }
`;

const NavText = styled.span`
  display: ${props => props.isCollapsed ? 'none' : 'block'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  background: linear-gradient(to bottom right, #f8fafc, #f1f5f9);
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px),
      linear-gradient(0deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
    background-size: 20px 20px;
    mask-image: radial-gradient(circle at 50% 50%, black, transparent);
    animation: gridMove 20s linear infinite;
  }

  @keyframes gridMove {
    0% { transform: translateY(0) }
    100% { transform: translateY(20px) }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  padding: 24px 32px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 24px;
    background: linear-gradient(to bottom, #3b82f6, #2563eb);
    border-radius: 4px;
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
    animation: titlePulse 2s infinite;
  }

  @keyframes titlePulse {
    0% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5) }
    50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.8) }
    100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5) }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 24px;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      #3b82f6 0%,
      #2563eb 50%,
      transparent 100%
    );
    animation: titleLine 3s infinite;
  }

  @keyframes titleLine {
    0% { transform: scaleX(0); opacity: 0 }
    50% { transform: scaleX(1); opacity: 1 }
    100% { transform: scaleX(0); opacity: 0 }
  }
`;

const AudioSection = styled.div`
  padding: 24px 32px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const RecordingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 16px;
  background: ${props => props.isRecording ? 
    'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))' : 
    'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))'};
  border: 1px solid ${props => props.isRecording ? 
    'rgba(239, 68, 68, 0.2)' : 
    'rgba(59, 130, 246, 0.2)'};
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

const TranscriptionContainer = styled.div`
  margin-top: 24px;
  padding: 20px;
  border-radius: 8px;
  background: #f9fafb;
  min-height: 200px;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.recording ? 
    'linear-gradient(135deg, #ef4444, #dc2626)' : 
    'linear-gradient(135deg, #3b82f6, #2563eb)'};
  color: white;
  border: none;
  box-shadow: 
    0 4px 6px -1px rgba(59, 130, 246, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
    0 0 20px rgba(59, 130, 246, 0.1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 
      0 6px 8px -1px rgba(59, 130, 246, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.2) inset,
      0 0 20px rgba(59, 130, 246, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
    transition: 0.5s;
    animation: buttonShine 3s infinite;
  }

  @keyframes buttonShine {
    0% { transform: translateX(-100%) rotate(45deg) }
    100% { transform: translateX(100%) rotate(45deg) }
  }

  ${props => props.recording && `
    animation: pulse 2s infinite;
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4) }
      70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0) }
      100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0) }
    }
  `}
`;

const CloseOutButton = styled(ActionButton)`
  background: #4f46e5;
  color: white;
  &:hover {
    background: #4338ca;
  }
`;

const TagContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
  color: #3b82f6;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(59, 130, 246, 0.2);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(59, 130, 246, 0.2),
      transparent
    );
    animation: tagShimmer 2s infinite;
  }

  @keyframes tagShimmer {
    0% { transform: translateX(0%) }
    100% { transform: translateX(200%) }
  }

  &::after {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    background: #3b82f6;
  }
`;

const SessionList = styled.div`
  margin-top: 24px;
`;

const SessionItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(148, 163, 184, 0.1);
  margin-bottom: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -1px rgba(0, 0, 0, 0.1);
  }
`;

const AudioPlayer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TranscriptionText = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: #374151;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Update the select component styling
const StyledSelect = styled.select`
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px;
  padding-right: 32px;

  &:hover {
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

// Update document tags
const DOCUMENT_TAGS = {
  MEETING_MINUTES: {
    tag: 'meeting_minutes',
    documentType: 'Meeting Minutes',
    description: 'Record and summarize staff meetings, parent conferences, and administrative discussions',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  DISCIPLINARY_ACTION: {
    tag: 'disciplinary_action',
    documentType: 'Disciplinary Action Plan',
    description: 'Document student behavior incidents and create action plans',
    requiredFields: {
      student_name: null,
      incident_date: null,
      behavior_description: null,
      action_taken: null,
      follow_up_date: null
    },
    promptTemplate: `
      This is a disciplinary action plan recording. Please analyze the content and extract:
      1. Student name(s) mentioned
      2. Date of incident
      3. Description of behavior/incident
      4. Immediate actions taken
      5. Required follow-up steps
      6. Scheduled follow-up date
      
      Format as specific action items that need to be completed.
      Include any parent contact requirements or documentation needs.
    `,
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )
  },
  CLASSROOM_OBSERVATION: {
    tag: 'classroom_observation',
    documentType: 'Classroom Observation Report',
    description: 'Record teacher observations and performance evaluations',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    )
  },
  EMAIL_DRAFT: {
    tag: 'email_draft',
    documentType: 'Email Draft',
    description: 'Create and review important email communications',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  },
  STUDENT_PROFILE: {
    tag: 'student_profile',
    documentType: 'Student Profile',
    description: 'Document student achievements, challenges, and development plans',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  },
  ANALYSIS_REPORT: {
    tag: 'analysis_report',
    documentType: 'Analysis Report',
    description: 'Generate comprehensive analysis of school data and trends',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )
  }
};

// Add new styled components for history view
const HistoryContainer = styled.div`
  padding: 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HistoryFilters = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  background: ${props => props.active ? '#4f46e5' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  
  &:hover {
    background: ${props => props.active ? '#4338ca' : '#f9fafb'};
  }
`;

const DateGroup = styled.div`
  margin-bottom: 32px;
`;

const DateHeader = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

// Add new styled components
const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  padding: 24px 32px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const AnalyticsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #4f46e5;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const SettingsSection = styled.div`
  padding: 24px 32px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SettingsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

// Add new styled components for Tasks
const TasksContainer = styled.div`
  padding: 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const TaskGroup = styled.div`
  margin-bottom: 32px;
`;

const TaskCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TaskTitle = styled.div`
  font-weight: 500;
  color: #111827;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const TaskStatus = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.status === 'completed' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.status === 'completed' ? '#059669' : '#dc2626'};
`;

// Add new styled components for document outputs
const DocumentPreview = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin: 20px 0;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Times New Roman', Times, serif;
`;

const DocumentHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`;

const DocumentTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #1f2937;
`;

const DocumentMeta = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const DocumentContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #1f2937;
`;

const EmailTemplate = styled(DocumentPreview)`
  font-family: Arial, sans-serif;
  max-width: 700px;
`;

const EmailHeader = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const EmailField = styled.div`
  margin-bottom: 12px;
  
  span {
    font-weight: bold;
    color: #4b5563;
    margin-right: 8px;
  }
`;

const EmailBody = styled.div`
  white-space: pre-line;
  line-height: 1.6;
`;

const ActionPlanTemplate = styled(DocumentPreview)`
  font-family: Arial, sans-serif;
`;

const ActionPlanSection = styled.div`
  margin-bottom: 24px;
  
  h3 {
    font-size: 18px;
    font-weight: bold;
    color: #1f2937;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const ActionPlanField = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    font-weight: bold;
    color: #4b5563;
    margin-bottom: 4px;
  }
  
  p {
    margin: 0;
    color: #1f2937;
  }
`;

const PrincipalAI = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioSessions, setAudioSessions] = useState([]);
  const [hasClosedOut, setHasClosedOut] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('recordings');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [autoCloseout, setAutoCloseout] = useState(true);
  const [theme, setTheme] = useState('light');
  const [defaultDocumentType, setDefaultDocumentType] = useState('General Note');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('General Note');
  const [recorder, setRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingInterval, setRecordingInterval] = useState(null);

  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
  const GEMINI_API_URL = process.env.REACT_APP_GEMINI_API_URL;
  const GOOGLE_SPEECH_API_KEY = process.env.REACT_APP_GOOGLE_SPEECH_API_KEY;
  const GOOGLE_SPEECH_API_URL = process.env.REACT_APP_GOOGLE_SPEECH_API_URL;

  useEffect(() => {
    // Check if user is authenticated and has a work email
    if (!currentUser?.email) {
      toast.error('Please log in with your work email to use Principal AI');
      navigate('/login', { state: { from: '/principal-ai' } });
      return;
    }

    // Load saved sessions specific to this user
    const savedSessions = localStorage.getItem(`audioSessions_${currentUser.email}`);
    if (savedSessions) {
      setAudioSessions(JSON.parse(savedSessions));
    }

    // Set up end of day reminder
    const now = new Date();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 17, 0, 0); // 5:00 PM
    
    if (now < endOfDay) {
      const timeout = endOfDay.getTime() - now.getTime();
      const timer = setTimeout(() => {
        toast.info('Would you like to close out your day?');
      }, timeout);
      
      return () => clearTimeout(timer);
    }
  }, [currentUser, navigate]);

  // Save sessions to localStorage whenever they change, keyed by user email
  useEffect(() => {
    if (currentUser?.email) {
      localStorage.setItem(`audioSessions_${currentUser.email}`, JSON.stringify(audioSessions));
    }
  }, [audioSessions, currentUser]);

  // Update recording control functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          volume: 1.0
        } 
      });
      setAudioStream(stream);
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
        bitsPerSecond: 128000
      });
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          console.log('Received audio chunk:', event.data.size, 'bytes');
        }
      };

      // Start duration counter
      const interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingInterval(interval);

      mediaRecorder.onstop = async () => {
        try {
          const completeBlob = new Blob(chunks, { type: 'audio/webm' });
          console.log('Final audio blob size:', completeBlob.size, 'bytes');
          setAudioChunks(chunks);
          
          // Convert to base64 for storage/transmission
          const reader = new FileReader();
          reader.readAsDataURL(completeBlob);
          reader.onloadend = () => {
            const base64Audio = reader.result;
            // Store the audio data with the session
            const newSession = {
              id: Date.now(),
              timestamp: new Date().toISOString(),
              audioData: base64Audio,
              duration: recordingDuration,
              documentType: selectedDocumentType,
              status: 'recording',
              userEmail: currentUser?.email,
              metadata: {
                browser: navigator.userAgent,
                recordingQuality: {
                  sampleRate: 16000,
                  channelCount: 1,
                  bitsPerSecond: 128000
                }
              }
            };
            console.log('Creating new session with metadata:', {
              ...newSession,
              audioData: `${base64Audio.substring(0, 50)}...` // Log truncated audio data
            });
            setAudioSessions(prev => [...prev, newSession]);
          };
        } catch (error) {
          console.error('Error processing audio after recording:', error);
          toast.error('Error processing recording');
        }
      };

      // Request data every 30 seconds to handle longer recordings
      mediaRecorder.start(30000);
      setRecorder(mediaRecorder);
      setIsRecording(true);
      setRecordingDuration(0);
      toast.success('Recording started');
      
      console.log('Recording started with settings:', {
        mimeType: mediaRecorder.mimeType,
        state: mediaRecorder.state,
        audioBitsPerSecond: mediaRecorder.audioBitsPerSecond
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = async () => {
    try {
      if (recorder && recorder.state === 'recording') {
        console.log('Stopping recording...');
        recorder.stop();
        const finalDuration = recordingDuration;
        console.log('Final recording duration:', formatDuration(finalDuration));
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => {
          track.stop();
          console.log('Audio track stopped:', track.label);
        });
        setAudioStream(null);
      }
      if (recordingInterval) {
        clearInterval(recordingInterval);
        setRecordingInterval(null);
      }
      setIsRecording(false);
      toast.success('Recording stopped');
    } catch (error) {
      console.error('Error stopping recording:', error);
      toast.error('Error stopping recording');
    }
  };

  // Format duration for display
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleTranscriptionComplete = async (transcribedText) => {
    try {
      setIsTranscribing(true);
      console.log('Received transcribed text:', transcribedText);

      // Get the document type configuration
      const documentConfig = Object.values(DOCUMENT_TAGS).find(
        dt => dt.documentType === selectedDocumentType
      );

      // Add user context and document-specific prompting to the analysis
      const prompt = `
        Analyze this transcription from a school principal's recording and extract key information.
        Principal's Email: ${currentUser.email}
        Document Type: ${selectedDocumentType}
        Available tags are: ${Object.values(DOCUMENT_TAGS).map(t => t.tag).join(', ')}

        ${documentConfig?.promptTemplate || ''}

        For each detected topic, you MUST use one of the exact tags listed above.
        If the content matches multiple document types, prioritize the most relevant one.
        
        Format the response as JSON with the following structure:
        {
          "analysis": "brief summary of the content",
          "documentType": "${selectedDocumentType}",
          "requiredFields": ${JSON.stringify(documentConfig?.requiredFields || {}, null, 2)},
          "actionItems": ["list of specific action items identified"],
          "tags": ["${documentConfig?.tag || 'meeting_minutes'}"]
        }

        Transcription to analyze:
        "${transcribedText}"

        IMPORTANT: Return ONLY the JSON object, without any markdown formatting or code block syntax.
      `;

      console.log('Sending to Gemini for analysis...');
      const { content: analysis, error } = await geminiApi.generateText(prompt);
      console.log('Gemini raw response:', { content: analysis, error });

      if (error) {
        console.error('Gemini API error:', error);
        throw new Error(error);
      }

      if (!analysis) {
        console.error('No analysis received from Gemini');
        throw new Error('No analysis received from Gemini');
      }

      // Clean and parse the response
      let cleanedResponse = analysis.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      console.log('Cleaned response:', cleanedResponse);
      
      try {
        const analysisData = JSON.parse(cleanedResponse);
        console.log('Parsed analysis data:', analysisData);
        
        // Create new session with enhanced metadata
        const newSession = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          userEmail: currentUser.email,
          transcription: transcribedText,
          analysis: analysisData.analysis,
          documentType: selectedDocumentType,
          requiredFields: analysisData.requiredFields || {},
          actionItems: analysisData.actionItems || [],
          tags: analysisData.tags || [],
          status: 'pending',
          metadata: {
            documentConfig: {
              type: documentConfig?.tag || 'general',
              fields: Object.keys(documentConfig?.requiredFields || {})
            },
            recordingDate: new Date().toISOString(),
            lastModified: new Date().toISOString()
          }
        };
        
        console.log('Creating new session:', newSession);
        setAudioSessions(prev => [...prev, newSession]);
        toast.success('Recording processed successfully!');
      } catch (parseError) {
        console.error('JSON parsing error:', parseError);
        console.error('Failed to parse response:', cleanedResponse);
        throw new Error('Failed to parse Gemini response as JSON');
      }
    } catch (error) {
      console.error('Detailed error in processing transcription:', error);
      console.error('Error stack:', error.stack);
      toast.error(`Failed to process recording: ${error.message}`);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleCloseOut = async () => {
    try {
      setIsTranscribing(true);
      
      // Enhanced end-of-day summary with document categorization
      const allTranscriptions = audioSessions.map(session => ({
        transcription: session.transcription,
        documentType: session.documentType,
        requiredFields: session.requiredFields
      }));

      const { content: summary, error } = await geminiApi.generateText(`
        Generate an end-of-day summary from these session transcriptions and categorize required follow-up documents.
        Principal's Email: ${currentUser.email}
        
        ${JSON.stringify(allTranscriptions)}
        
        Format the response as JSON with:
        {
          "summary": "executive summary of the day",
          "documents": [{
            "type": "document type",
            "status": "draft/ready for review",
            "priority": "high/medium/low",
            "deadline": "suggested deadline"
          }],
          "actionItems": ["consolidated list of action items"],
          "followUp": ["items requiring immediate follow-up"]
        }
      `);

      if (error) {
        throw new Error(error);
      }

      const summaryData = JSON.parse(summary);

      // Send email using the API
      const emailResponse = await fetch(`${API_URL}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: currentUser.email,
          subject: `Principal AI - Daily Summary ${new Date().toLocaleDateString()}`,
          content: `
            Daily Summary: ${summaryData.summary}

            Documents to Prepare:
            ${summaryData.documents.map(doc => `
              - ${doc.type} (${doc.priority} priority)
                Status: ${doc.status}
                Deadline: ${doc.deadline}
            `).join('\n')}

            Action Items:
            ${summaryData.actionItems.map(item => `- ${item}`).join('\n')}

            Immediate Follow-up Required:
            ${summaryData.followUp.map(item => `- ${item}`).join('\n')}
          `
        })
      });

      if (!emailResponse.ok) {
        throw new Error('Failed to send email summary');
      }

      // Update status of processed sessions
      setAudioSessions(prev => 
        prev.map(session => ({
          ...session,
          status: 'processed'
        }))
      );

      toast.success('Daily summary has been sent to your email.');
    } catch (error) {
      console.error('Error during close out:', error);
      toast.error('Error processing daily summary. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Filter and group sessions by date
  const getFilteredSessions = () => {
    let filtered = [...audioSessions];
    
    // Apply tag filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(session => 
        session.documentType === selectedFilter
      );
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(session =>
        session.transcription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.analysis.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Group by date
    return filtered.reduce((groups, session) => {
      const date = new Date(session.timestamp).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(session);
      return groups;
    }, {});
  };

  // Get unique document types for filter
  const documentTypes = ['all', ...new Set(audioSessions.map(s => s.documentType))];

  const navigationItems = [
    {
      section: 'Main',
      items: [
        {
          id: 'recordings',
          label: 'Recordings',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v8m-4-4h8m-4 10c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
            </svg>
          ),
        },
        {
          id: 'history',
          label: 'History',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        },
        {
          id: 'tasks',
          label: 'Tasks',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          ),
        },
      ],
    },
    {
      section: 'Management',
      items: [
        {
          id: 'analytics',
          label: 'Analytics',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        },
      ],
    },
  ];

  // Analytics calculations
  const getAnalyticsData = () => {
    const today = new Date();
    const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const recentSessions = audioSessions.filter(
      session => new Date(session.timestamp) > last7Days
    );

    const documentTypeCounts = audioSessions.reduce((acc, session) => {
      acc[session.documentType] = (acc[session.documentType] || 0) + 1;
      return acc;
    }, {});

    const dailySessionCounts = recentSessions.reduce((acc, session) => {
      const date = new Date(session.timestamp).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const actionItemsCount = audioSessions.reduce(
      (sum, session) => sum + (session.actionItems?.length || 0),
      0
    );

    return {
      totalSessions: audioSessions.length,
      processedSessions: audioSessions.filter(s => s.status === 'processed').length,
      documentTypeCounts,
      dailySessionCounts,
      actionItemsCount,
      averageSessionLength: audioSessions.length ? 
        (audioSessions.reduce((sum, s) => sum + s.transcription.length, 0) / audioSessions.length / 100).toFixed(1) :
        0
    };
  };

  const renderAnalytics = () => {
    const analytics = getAnalyticsData();
    const chartData = {
      labels: Object.keys(analytics.documentTypeCounts),
      datasets: [{
        label: 'Sessions by Type',
        data: Object.values(analytics.documentTypeCounts),
        backgroundColor: [
          '#4f46e5',
          '#7c3aed',
          '#2563eb',
          '#0891b2',
          '#0d9488',
          '#059669',
          '#16a34a'
        ]
      }]
    };

    const activityData = {
      labels: Object.keys(analytics.dailySessionCounts),
      datasets: [{
        label: 'Daily Sessions',
        data: Object.values(analytics.dailySessionCounts),
        borderColor: '#4f46e5',
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };

    const actionItemsByType = audioSessions.reduce((acc, session) => {
      if (session.documentType && session.actionItems) {
        acc[session.documentType] = (acc[session.documentType] || 0) + session.actionItems.length;
      }
      return acc;
    }, {});

    const actionItemsChartData = {
      labels: Object.keys(actionItemsByType),
      datasets: [{
        label: 'Action Items by Document Type',
        data: Object.values(actionItemsByType),
        backgroundColor: '#2563eb',
        borderRadius: 8,
      }]
    };

    return (
      <div className="w-full">
        <div className="bg-white p-6 rounded-lg shadow-sm mx-8 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
          <StatGrid>
            <StatCard>
              <StatValue>{analytics.totalSessions}</StatValue>
              <StatLabel>Total Sessions</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{analytics.processedSessions}</StatValue>
              <StatLabel>Processed Sessions</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{analytics.actionItemsCount}</StatValue>
              <StatLabel>Action Items Created</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{analytics.averageSessionLength}m</StatValue>
              <StatLabel>Avg. Session Length</StatLabel>
            </StatCard>
          </StatGrid>
        </div>

        <AnalyticsGrid>
          <AnalyticsCard>
            <CardTitle>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Sessions by Type
            </CardTitle>
            <div style={{ height: '300px' }}>
              <Doughnut 
                data={chartData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 20,
                        usePointStyle: true
                      }
                    }
                  }
                }}
              />
            </div>
          </AnalyticsCard>

          <AnalyticsCard>
            <CardTitle>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Activity Trend
            </CardTitle>
            <div style={{ height: '300px' }}>
              <Line 
                data={activityData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1
                      }
                    }
                  }
                }}
              />
            </div>
          </AnalyticsCard>

          <AnalyticsCard>
            <CardTitle>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Action Items by Document Type
            </CardTitle>
            <div style={{ height: '300px' }}>
              <Bar
                data={actionItemsChartData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1
                      }
                    }
                  }
                }}
              />
            </div>
          </AnalyticsCard>
        </AnalyticsGrid>

        <div className="bg-white p-6 rounded-lg shadow-sm mx-8 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {audioSessions.slice(0, 5).map(session => (
              <div key={session.id} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
                <div>
                  <div className="font-medium">{session.documentType}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(session.timestamp).toLocaleDateString()} at {new Date(session.timestamp).toLocaleTimeString()}
                  </div>
                </div>
                <Tag>{session.status}</Tag>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <SettingsSection>
      <SettingsCard>
        <SettingsTitle>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          Notifications
        </SettingsTitle>
        <div className="space-y-6">
          <SettingItem>
            <div>
              <div className="font-medium">Email Notifications</div>
              <div className="text-sm text-gray-500">Receive daily summaries and alerts</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer ${emailNotifications ? 'bg-blue-600' : ''} peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
            </label>
          </SettingItem>
          <SettingItem>
            <div>
              <div className="font-medium">Automatic Close-out</div>
              <div className="text-sm text-gray-500">Process recordings at end of day</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={autoCloseout}
                onChange={(e) => setAutoCloseout(e.target.checked)}
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer ${autoCloseout ? 'bg-blue-600' : ''} peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
            </label>
          </SettingItem>
        </div>
      </SettingsCard>

      <SettingsCard>
        <SettingsTitle>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          Preferences
        </SettingsTitle>
        <div className="space-y-6">
          <SettingItem>
            <div>
              <div className="font-medium">Theme</div>
              <div className="text-sm text-gray-500">Choose your preferred theme</div>
            </div>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System</option>
            </select>
          </SettingItem>
          <SettingItem>
            <div>
              <div className="font-medium">Default Document Type</div>
              <div className="text-sm text-gray-500">Set default for new recordings</div>
            </div>
            <select
              value={defaultDocumentType}
              onChange={(e) => setDefaultDocumentType(e.target.value)}
              className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {Object.values(DOCUMENT_TAGS).map(tag => (
                <option key={tag.tag} value={tag.documentType}>
                  {tag.documentType}
                </option>
              ))}
            </select>
          </SettingItem>
        </div>
      </SettingsCard>

      <SettingsCard>
        <SettingsTitle>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Account
        </SettingsTitle>
        <div className="space-y-6">
          <div>
            <div className="font-medium">{currentUser?.email}</div>
            <div className="text-sm text-gray-500">Principal Account</div>
          </div>
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard>
        <SettingsTitle>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Data Management
        </SettingsTitle>
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            Manage your recorded sessions and application data
          </div>
          <div className="space-x-4">
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all recordings? This cannot be undone.')) {
                  setAudioSessions([]);
                  localStorage.removeItem(`audioSessions_${currentUser.email}`);
                  toast.success('All recordings have been cleared');
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear All Recordings
            </button>
            <button
              onClick={() => {
                const data = JSON.stringify(audioSessions, null, 2);
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `principal-ai-export-${new Date().toISOString()}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Export Data
            </button>
          </div>
        </div>
      </SettingsCard>
    </SettingsSection>
  );

  // Add new function to get tasks from sessions
  const getTasksFromSessions = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todaysSessions = audioSessions.filter(session => {
      const sessionDate = new Date(session.timestamp);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === today.getTime();
    });

    const pendingTasks = [];
    const completedTasks = [];

    todaysSessions.forEach(session => {
      if (session.actionItems) {
        session.actionItems.forEach(item => {
          const task = {
            id: Math.random().toString(36).substr(2, 9),
            content: item,
            sessionId: session.id,
            documentType: session.documentType,
            timestamp: session.timestamp,
            status: session.status === 'processed' ? 'completed' : 'pending'
          };
          
          if (session.status === 'processed') {
            completedTasks.push(task);
          } else {
            pendingTasks.push(task);
          }
        });
      }
    });

    return {
      pending: pendingTasks,
      completed: completedTasks
    };
  };

  // Add renderTasks function
  const renderTasks = () => {
    const { pending, completed } = getTasksFromSessions();
    
    const renderDocument = (task) => {
      const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      };

      switch (task.documentType) {
        case 'Email Draft':
          return (
            <EmailTemplate>
              <EmailHeader>
                <EmailField>
                  <span>To:</span> {task.requiredFields?.recipient || '[Recipient]'}
                </EmailField>
                <EmailField>
                  <span>From:</span> {currentUser?.email}
                </EmailField>
                <EmailField>
                  <span>Date:</span> {formatDate(task.timestamp)}
                </EmailField>
                <EmailField>
                  <span>Subject:</span> {task.requiredFields?.subject || task.content.split('\n')[0]}
                </EmailField>
              </EmailHeader>
              <EmailBody>
                {task.content}
                
                <div style={{ marginTop: '24px' }}>
                  Best regards,<br />
                  {currentUser?.name || currentUser?.email.split('@')[0]}<br />
                  Principal
                </div>
              </EmailBody>
            </EmailTemplate>
          );

        case 'Disciplinary Action Plan':
          return (
            <ActionPlanTemplate>
              <DocumentHeader>
                <DocumentTitle>Student Disciplinary Action Plan</DocumentTitle>
                <DocumentMeta>Date: {formatDate(task.timestamp)}</DocumentMeta>
                <DocumentMeta>Reference: {task.id}</DocumentMeta>
              </DocumentHeader>
              
              <ActionPlanSection>
                <h3>Student Information</h3>
                <ActionPlanField>
                  <label>Student Name:</label>
                  <p>{task.requiredFields?.student_name || '[Student Name]'}</p>
                </ActionPlanField>
                <ActionPlanField>
                  <label>Grade/Class:</label>
                  <p>{task.requiredFields?.grade || '[Grade Level]'}</p>
                </ActionPlanField>
              </ActionPlanSection>

              <ActionPlanSection>
                <h3>Incident Details</h3>
                <ActionPlanField>
                  <label>Date of Incident:</label>
                  <p>{task.requiredFields?.incident_date || formatDate(task.timestamp)}</p>
                </ActionPlanField>
                <ActionPlanField>
                  <label>Description of Behavior:</label>
                  <p>{task.requiredFields?.behavior_description || task.content}</p>
                </ActionPlanField>
              </ActionPlanSection>

              <ActionPlanSection>
                <h3>Action Plan</h3>
                <ActionPlanField>
                  <label>Immediate Actions Taken:</label>
                  <p>{task.requiredFields?.action_taken || 'Pending'}</p>
                </ActionPlanField>
                <ActionPlanField>
                  <label>Required Follow-up:</label>
                  <p>{task.requiredFields?.follow_up || 'To be determined'}</p>
                </ActionPlanField>
                <ActionPlanField>
                  <label>Follow-up Date:</label>
                  <p>{task.requiredFields?.follow_up_date || 'To be scheduled'}</p>
                </ActionPlanField>
              </ActionPlanSection>

              <ActionPlanSection>
                <h3>Signatures</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '32px' }}>
                  <div>
                    <p>____________________________</p>
                    <p>Principal</p>
                    <p>Date: {formatDate(task.timestamp)}</p>
                  </div>
                  <div>
                    <p>____________________________</p>
                    <p>Parent/Guardian</p>
                    <p>Date: ________________</p>
                  </div>
                </div>
              </ActionPlanSection>
            </ActionPlanTemplate>
          );

        case 'Meeting Minutes':
          return (
            <DocumentPreview>
              <DocumentHeader>
                <DocumentTitle>Meeting Minutes</DocumentTitle>
                <DocumentMeta>Date: {formatDate(task.timestamp)}</DocumentMeta>
                <DocumentMeta>Location: {task.requiredFields?.location || 'School Campus'}</DocumentMeta>
              </DocumentHeader>
              
              <DocumentContent>
                <h3 style={{ marginBottom: '16px' }}>Attendees</h3>
                <p style={{ marginBottom: '24px' }}>{task.requiredFields?.attendees || 'Meeting participants'}</p>
                
                <h3 style={{ marginBottom: '16px' }}>Discussion Points</h3>
                <div style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}>{task.content}</div>
                
                <h3 style={{ marginBottom: '16px' }}>Action Items</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  {task.actionItems?.map((item, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>{item}</li>
                  ))}
                </ul>
              </DocumentContent>
            </DocumentPreview>
          );

        default:
          return (
            <DocumentPreview>
              <DocumentHeader>
                <DocumentTitle>{task.documentType}</DocumentTitle>
                <DocumentMeta>Date: {formatDate(task.timestamp)}</DocumentMeta>
              </DocumentHeader>
              <DocumentContent>
                {task.content}
              </DocumentContent>
            </DocumentPreview>
          );
      }
    };

    return (
      <TasksContainer>
        {pending.length > 0 && (
          <TaskGroup>
            <DateHeader>Pending Documents</DateHeader>
            {pending.map(task => (
              <div key={task.id}>
                <TaskCard>
                  <TaskHeader>
                    <TaskTitle>{task.documentType}</TaskTitle>
                    <TaskMeta>
                      <TaskStatus status="pending">Pending</TaskStatus>
                      <span>{new Date(task.timestamp).toLocaleTimeString()}</span>
                    </TaskMeta>
                  </TaskHeader>
                </TaskCard>
                {renderDocument(task)}
              </div>
            ))}
          </TaskGroup>
        )}

        {completed.length > 0 && (
          <TaskGroup>
            <DateHeader>Completed Documents</DateHeader>
            {completed.map(task => (
              <div key={task.id}>
                <TaskCard>
                  <TaskHeader>
                    <TaskTitle>{task.documentType}</TaskTitle>
                    <TaskMeta>
                      <TaskStatus status="completed">Completed</TaskStatus>
                      <span>{new Date(task.timestamp).toLocaleTimeString()}</span>
                    </TaskMeta>
                  </TaskHeader>
                </TaskCard>
                {renderDocument(task)}
              </div>
            ))}
          </TaskGroup>
        )}

        {pending.length === 0 && completed.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents for today</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start recording to generate documents
            </p>
          </div>
        )}
      </TasksContainer>
    );
  };

  return (
    <Container>
      <Sidebar isCollapsed={isCollapsed}>
        <SidebarHeader isCollapsed={isCollapsed}>
          {!isCollapsed && <span style={{ fontSize: '18px', fontWeight: '500' }}>Principal AI</span>}
          <CollapseButton onClick={() => setIsCollapsed(!isCollapsed)}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              {isCollapsed ? (
                <path d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
              )}
            </svg>
          </CollapseButton>
        </SidebarHeader>

        {navigationItems.map((section) => (
          <NavSection key={section.section} isCollapsed={isCollapsed}>
            <NavTitle isCollapsed={isCollapsed}>{section.section}</NavTitle>
            {section.items.map((item) => (
              <NavItem
                key={item.id}
                active={activeView === item.id}
                isCollapsed={isCollapsed}
                onClick={() => setActiveView(item.id)}
              >
                {item.icon}
                <NavText isCollapsed={isCollapsed}>{item.label}</NavText>
              </NavItem>
            ))}
          </NavSection>
        ))}
      </Sidebar>

      <MainContent>
        <Header>
          <PageTitle>
            {navigationItems
              .flatMap(section => section.items)
              .find(item => item.id === activeView)?.label || 'Recordings'}
          </PageTitle>
          <div className="flex items-center gap-4">
            {activeView === 'recordings' && (
              <>
                <ActionButton
                  recording={isRecording}
                  onClick={isRecording ? stopRecording : startRecording}
                >
                  {isRecording ? (
                    <>
                      <svg className="w-5 h-5 mr-2 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                      Stop Recording ({formatDuration(recordingDuration)})
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Start Recording
                    </>
                  )}
                </ActionButton>
                <StyledSelect
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                >
                  {Object.values(DOCUMENT_TAGS).map(tag => (
                    <option key={tag.tag} value={tag.documentType}>
                      {tag.documentType}
                    </option>
                  ))}
                </StyledSelect>
              </>
            )}
          </div>
        </Header>

        <ContentArea>
          {activeView === 'recordings' && (
            <div className="space-y-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Record Meeting or Discussion
                </label>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-500">
                    {isRecording ? (
                      <span className="text-red-600 font-medium">Recording in progress: {formatDuration(recordingDuration)}</span>
                    ) : (
                      'Click to start recording your session...'
                    )}
                  </div>
                  {isRecording && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse mr-2"></div>
                      <span className="text-sm text-gray-600">Recording</span>
                    </div>
                  )}
                </div>
                <VoiceRecorder 
                  onTranscriptionComplete={handleTranscriptionComplete}
                  placeholder="Click to start recording your session..."
                  isRecording={isRecording}
                  onStartRecording={startRecording}
                  onStopRecording={stopRecording}
                  selectedDocumentType={selectedDocumentType}
                  recordingDuration={recordingDuration}
                />
              </div>

              <SessionList>
                {audioSessions.map(session => (
                  <SessionItem key={session.id}>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                      {new Date(session.timestamp).toLocaleTimeString()}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <Tag>{session.documentType}</Tag>
                      <span style={{ 
                        fontSize: '14px',
                        color: session.status === 'processed' ? '#059669' : '#4f46e5',
                        fontWeight: '500'
                      }}>
                        {session.status === 'processed' ? 'Processed' : 'Pending'}
                      </span>
                    </div>
                    <TranscriptionText>
                      {session.transcription}
                    </TranscriptionText>
                    {session.analysis && (
                      <div style={{ marginTop: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ fontWeight: 500, marginBottom: '8px' }}>Analysis</div>
                        <div style={{ marginBottom: '12px' }}>{session.analysis}</div>
                        
                        {Object.entries(session.requiredFields).length > 0 && (
                          <>
                            <div style={{ fontWeight: 500, marginBottom: '8px' }}>Required Information</div>
                            <div style={{ marginBottom: '12px' }}>
                              {Object.entries(session.requiredFields).map(([field, value]) => (
                                <div key={field} style={{ marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 500 }}>{field.replace(/_/g, ' ')}: </span>
                                  {value}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        
                        {session.actionItems?.length > 0 && (
                          <>
                            <div style={{ fontWeight: 500, marginBottom: '8px' }}>Action Items</div>
                            <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                              {session.actionItems.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </SessionItem>
                ))}
              </SessionList>
            </div>
          )}

          {activeView === 'history' && (
            <div className="space-y-6">
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search transcriptions..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <HistoryFilters>
                {documentTypes.map(type => (
                  <FilterButton
                    key={type}
                    active={selectedFilter === type}
                    onClick={() => setSelectedFilter(type)}
                  >
                    {type === 'all' ? 'All Types' : type}
                  </FilterButton>
                ))}
              </HistoryFilters>

              {Object.entries(getFilteredSessions())
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                .map(([date, sessions]) => (
                  <DateGroup key={date}>
                    <DateHeader>{new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</DateHeader>
                    <SessionList>
                      {sessions
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                        .map(session => (
                          <SessionItem key={session.id}>
                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                              {new Date(session.timestamp).toLocaleTimeString()}
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <Tag>{session.documentType}</Tag>
                              <span style={{ 
                                fontSize: '14px',
                                color: session.status === 'processed' ? '#059669' : '#4f46e5',
                                fontWeight: '500'
                              }}>
                                {session.status === 'processed' ? 'Processed' : 'Pending'}
                              </span>
                            </div>
                            <TranscriptionText>
                              {session.transcription}
                            </TranscriptionText>
                            {session.analysis && (
                              <div style={{ marginTop: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                                <div style={{ fontWeight: 500, marginBottom: '8px' }}>Analysis</div>
                                <div style={{ marginBottom: '12px' }}>{session.analysis}</div>
                                
                                {Object.entries(session.requiredFields).length > 0 && (
                                  <>
                                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>Required Information</div>
                                    <div style={{ marginBottom: '12px' }}>
                                      {Object.entries(session.requiredFields).map(([field, value]) => (
                                        <div key={field} style={{ marginBottom: '4px' }}>
                                          <span style={{ fontWeight: 500 }}>{field.replace(/_/g, ' ')}: </span>
                                          {value}
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                                
                                {session.actionItems?.length > 0 && (
                                  <>
                                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>Action Items</div>
                                    <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                                      {session.actionItems.map((item, index) => (
                                        <li key={index}>{item}</li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </div>
                            )}
                          </SessionItem>
                      ))}
                    </SessionList>
                  </DateGroup>
              ))}

              {Object.keys(getFilteredSessions()).length === 0 && (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No recordings found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm ? 'Try adjusting your search terms' : 'Start recording to see your history'}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeView === 'analytics' && (
            <div className="space-y-6">
              {renderAnalytics()}
            </div>
          )}

          {activeView === 'settings' && (
            <div className="space-y-6">
              {renderSettings()}
            </div>
          )}

          {activeView === 'tasks' && renderTasks()}
        </ContentArea>
      </MainContent>

      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Container>
  );
};

export default PrincipalAI; 